import React, { useState, useEffect } from 'react';
import { getApiEndpoint } from '../../../config/api';
import { Link, useNavigate } from 'react-router-dom';
import '../Contractor/ContractorProfile.css';
import allSafeLogo from '../../../assets/cloudsafe-logo.png';
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaEdit,
  FaSave,
  FaTimes,
  FaArrowLeft,
  FaKey,
  FaShieldAlt,
  FaCopy,
  FaEye
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import DigitalBadge from '../../compliance/DigitalBadge/DigitalBadge';

const ProfileSettings = () => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    role: '',
    cloudSafeId: ''
  });
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [error, setError] = useState(null);
  const [showIdBadge, setShowIdBadge] = useState(false);

  useEffect(() => {
    // Verify user is staff
    const userRole = getUserRole();
    if (userRole !== 'staff') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    fetchProfile();
  }, [navigate]);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint('/api/users/profile'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Generate CloudSafe ID based on user data
        const cloudSafeId = `CS-STAFF-${Date.now().toString().slice(-6)}`;
        const profileData = {
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phoneNumber: data.phoneNumber || '',
          role: data.role || '',
          cloudSafeId: cloudSafeId
        };
        setProfile(profileData);
        setEditForm({
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          phoneNumber: profileData.phoneNumber
        });
      } else {
        const errorText = await response.text();
        setError(`Failed to fetch profile. Status: ${response.status} ${response.statusText}. Response: ${errorText}`);
        console.error('Profile fetch error:', response.status, response.statusText, errorText);
        throw new Error('Failed to fetch profile');
      }
    } catch (error) {
      setError(error.message || 'Failed to load profile data');
      console.error('Profile fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/users/update-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });
      if (response.ok) {
        setProfile(prev => ({ ...prev, ...editForm }));
        setIsEditing(false);
        toast.success('Profile updated successfully');
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/users/change-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });
      if (response.ok) {
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordForm(false);
        toast.success('Password updated successfully');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update password');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update password');
    }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const copyCloudSafeId = () => {
    navigator.clipboard.writeText(profile.cloudSafeId);
    toast.success('CloudSafe ID copied to clipboard');
  };

  if (isLoading) {
    return (
      <div className="contractor-profile">
        <header className="profile-header">
          <div className="header-left">
            <button onClick={() => navigate('/staff/dashboard')} className="back-button">
              <FaArrowLeft /> Back to Dashboard
            </button>
            <img src={allSafeLogo} alt="CloudSafe" className="logo" />
            <h1>Staff Profile</h1>
          </div>
        </header>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
          {error && <div style={{color: 'red', marginTop: 16}}>{error}</div>}
        </div>
      </div>
    );
  }

  if (showIdBadge) {
    return <DigitalBadge onBack={() => setShowIdBadge(false)} />;
  }

  return (
    <div className="contractor-profile">
      <header className="profile-header">
        <div className="header-left">
          <button
            onClick={() => navigate('/staff/dashboard')}
            className="back-button"
            style={{ pointerEvents: 'auto', opacity: 1 }}
          >
            <FaArrowLeft /> Back to Dashboard
          </button>
          <img src={allSafeLogo} alt="CloudSafe" className="logo" />
          <h1>Staff Profile</h1>
        </div>
        <div className="header-right">
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>
      {isLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
          {error && <div style={{ color: 'red', marginTop: 16 }}>{error}</div>}
        </div>
      ) : (
        <>
          {error && <div style={{color: 'red', margin: 16, fontWeight: 600}}>{error}</div>}
          <div className="profile-container">
            <div className="profile-card">
              <div className="card-header">
                <div className="profile-avatar">
                  <FaUser />
                </div>
                <div className="profile-info">
                  <h2>{profile.firstName} {profile.lastName}</h2>
                  <span className="role-badge">Staff</span>
                </div>
              </div>

              <div className="card-content">
                {/* CloudSafe ID Section */}
                <div className="section cloudsafe-id-section">
                  <h3><FaIdCard /> CloudSafe ID</h3>
                  <div className="cloudsafe-id-container">
                    <div className="id-display">
                      <span className="id-label">Your unique CloudSafe ID:</span>
                      <div className="id-value">
                        <code>{profile.cloudSafeId}</code>
                        <div className="id-actions">
                          <button onClick={copyCloudSafeId} className="copy-btn" title="Copy to clipboard">
                            <FaCopy />
                          </button>
                          <button onClick={() => setShowIdBadge(true)} className="show-badge-btn" title="Show ID Badge">
                            <FaEye /> Show ID Badge
                          </button>
                        </div>
                      </div>
                    </div>
                    <p className="id-description">
                      Use this ID when communicating with colleagues or support. This ID identifies you as a verified CloudSafe staff member.
                    </p>
                  </div>
                </div>

                {/* Profile Information Section */}
                <div className="section profile-info-section">
                  <div className="section-header">
                    <h3><FaUser /> Profile Information</h3>
                    {!isEditing && (
                      <button 
                        onClick={() => setIsEditing(true)}
                        className="edit-btn"
                      >
                        <FaEdit /> Edit
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <form onSubmit={handleEditSubmit} className="edit-form">
                      <div className="form-row">
                        <div className="form-group">
                          <label>First Name</label>
                          <input
                            type="text"
                            value={editForm.firstName}
                            onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Last Name</label>
                          <input
                            type="text"
                            value={editForm.lastName}
                            onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Phone Number</label>
                        <input
                          type="tel"
                          value={editForm.phoneNumber}
                          onChange={(e) => setEditForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                          placeholder="Enter your phone number"
                        />
                      </div>
                      <div className="form-actions">
                        <button type="submit" className="save-btn">
                          <FaSave /> Save Changes
                        </button>
                        <button 
                          type="button" 
                          onClick={() => {
                            setIsEditing(false);
                            setEditForm({
                              firstName: profile.firstName,
                              lastName: profile.lastName,
                              phoneNumber: profile.phoneNumber
                            });
                          }}
                          className="cancel-btn"
                        >
                          <FaTimes /> Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="profile-display">
                      <div className="info-item">
                        <FaUser className="icon" />
                        <div>
                          <label>Full Name</label>
                          <span>{profile.firstName} {profile.lastName}</span>
                        </div>
                      </div>
                      <div className="info-item">
                        <FaEnvelope className="icon" />
                        <div>
                          <label>Email Address</label>
                          <span>{profile.email}</span>
                          <small>Email cannot be changed. Contact support if needed.</small>
                        </div>
                      </div>
                      <div className="info-item">
                        <FaPhone className="icon" />
                        <div>
                          <label>Phone Number</label>
                          <span>{profile.phoneNumber || 'Not provided'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Security Section */}
                <div className="section security-section">
                  <div className="section-header">
                    <h3><FaShieldAlt /> Security</h3>
                    {!showPasswordForm && (
                      <button 
                        onClick={() => setShowPasswordForm(true)}
                        className="change-password-btn"
                      >
                        <FaKey /> Change Password
                      </button>
                    )}
                  </div>
                  {showPasswordForm && (
                    <form onSubmit={handlePasswordSubmit} className="password-form">
                      <div className="form-group">
                        <label>Current Password</label>
                        <input
                          type="password"
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>New Password</label>
                        <input
                          type="password"
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                          required
                          minLength="6"
                        />
                      </div>
                      <div className="form-group">
                        <label>Confirm New Password</label>
                        <input
                          type="password"
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          required
                          minLength="6"
                        />
                      </div>
                      <div className="form-actions">
                        <button type="submit" className="save-btn">
                          <FaSave /> Update Password
                        </button>
                        <button 
                          type="button" 
                          onClick={() => {
                            setShowPasswordForm(false);
                            setPasswordForm({
                              currentPassword: '',
                              newPassword: '',
                              confirmPassword: ''
                            });
                          }}
                          className="cancel-btn"
                        >
                          <FaTimes /> Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProfileSettings; 